import { useState } from 'react';
import {
  Bell,
  Check,
  Trash2,
  Filter,
  Clock,
  CheckCircle,
  X,
  ArrowRight,
  ChevronDown,
  PawPrint,
} from 'lucide-react';
import {
  useNotifications,
  NotificationType,
} from '../context/NotificationsContext';
import { usePets } from '../context/PetsContext';
import { formatDistanceToNow } from '../utils/formatTime';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface NotificationsProps {
  onClose: () => void;
  onNavigateToPendingApprovals?: (petId: string, proposalId?: string) => void;
}

export function Notifications({
  onClose,
  onNavigateToPendingApprovals,
}: NotificationsProps) {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    getActionRequiredNotifications,
  } = useNotifications();
  const { pets } = usePets();

  const [filter, setFilter] = useState<'all' | 'unread' | 'action-required'>(
    'all'
  );
  /** Selected pet IDs for filter; empty = all pets */
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>([]);

  const togglePetSelection = (petId: string) => {
    setSelectedPetIds((prev) =>
      prev.includes(petId)
        ? prev.filter((id) => id !== petId)
        : [...prev, petId]
    );
  };

  const selectAllPets = () => setSelectedPetIds([]);
  const isAllPetsSelected = selectedPetIds.length === 0;
  const selectedCount = selectedPetIds.length;
  const petFilterLabel = isAllPetsSelected
    ? 'All pets'
    : selectedCount === 0
      ? 'All pets'
      : selectedCount === 1
        ? (pets.find((p) => p.id === selectedPetIds[0])?.name ?? '1 pet')
        : `${selectedCount} pets`;

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'proposal_received':
        return '📬';
      case 'proposal_approved':
        return '✅';
      case 'proposal_rejected':
        return '❌';
      case 'edit_requested':
        return '✏️';
      case 'appointment_upcoming':
      case 'appointment_reminder':
        return '📅';
      case 'appointment_confirmed':
        return '✔️';
      case 'vaccine_due':
        return '⚠️';
      case 'access_granted':
        return '🔓';
      case 'access_revoked':
        return '🔒';
      case 'document_shared':
        return '📄';
      case 'reminder':
        return '⏰';
      case 'reward_earned':
        return '🪙';
      case 'social':
        return '👥';
      case 'pet_parks':
        return '🗺️';
      case 'record_added':
        return '📋';
      case 'system':
        return '🔔';
      default:
        return '📣';
    }
  };

  const filteredNotifications = notifications.filter((notif) => {
    if (filter === 'unread' && notif.read) return false;
    if (filter === 'action-required' && (!notif.actionRequired || notif.read))
      return false;
    if (selectedPetIds.length > 0) {
      if (!notif.petId) return true;
      if (!selectedPetIds.includes(notif.petId)) return false;
    }
    return true;
  });

  const handleNotificationClick = (notif: {
    id: string;
    actionLink?: string;
    petId?: string;
    proposalId?: string;
  }) => {
    markAsRead(notif.id);
    if (
      notif.actionLink === 'pending-approvals' &&
      onNavigateToPendingApprovals &&
      notif.petId
    ) {
      onNavigateToPendingApprovals(notif.petId, notif.proposalId);
    }
  };

  const actionRequiredCount = getActionRequiredNotifications().length;

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-[#0B0A12] dark:to-[#0F0E16]">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 shadow-lg">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Bell className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl">Notifications</h1>
                <p className="text-sm text-blue-100">
                  {unreadCount} unread{' '}
                  {unreadCount === 1 ? 'notification' : 'notifications'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors text-sm"
              >
                <CheckCircle className="w-4 h-4" />
                Mark All Read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#16151D] border-b border-gray-200 dark:border-white/8 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400 dark:text-white/40 shrink-0" />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  filter === 'unread'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilter('action-required')}
                className={`px-4 py-2 rounded-lg text-sm transition-colors relative ${
                  filter === 'action-required'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                Action Required ({actionRequiredCount})
                {actionRequiredCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">
                    {actionRequiredCount}
                  </span>
                )}
              </button>
            </div>
            {pets.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/10 border border-transparent hover:border-gray-300 dark:hover:border-white/10"
                    type="button"
                  >
                    <PawPrint className="w-4 h-4" />
                    <span>{petFilterLabel}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Filter by pet</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuCheckboxItem
                    checked={isAllPetsSelected}
                    onCheckedChange={selectAllPets}
                  >
                    All pets
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  {pets.map((pet) => (
                    <DropdownMenuCheckboxItem
                      key={pet.id}
                      checked={selectedPetIds.includes(pet.id)}
                      onCheckedChange={() => togglePetSelection(pet.id)}
                    >
                      {pet.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-5xl mx-auto space-y-3">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-16 h-16 mx-auto text-gray-300 dark:text-white/20 mb-4" />
              <p className="text-gray-500 dark:text-white/70 text-lg mb-2">
                No notifications
              </p>
              <p className="text-sm text-gray-400 dark:text-white/50">
                {selectedPetIds.length > 0
                  ? 'No notifications for selected pets'
                  : filter === 'unread'
                    ? "You're all caught up!"
                    : filter === 'action-required'
                      ? 'No actions required at this time'
                      : "You'll see updates and notifications here"}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                className={`border rounded-lg p-4 transition-all ${
                  !notif.read
                    ? 'border-l-4 border-l-blue-600 shadow-md bg-white dark:bg-[#16151D] dark:border-white/8'
                    : 'opacity-75 hover:opacity-100 bg-white dark:bg-[#16151D] border-gray-200 dark:border-white/8'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="text-3xl flex-shrink-0">
                    {notif.icon || getNotificationIcon(notif.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4 mb-1">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium dark:text-white">
                            {notif.title}
                          </h3>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                          )}
                          {notif.actionRequired && (
                            <span className="bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded text-xs font-medium">
                              Action Required
                            </span>
                          )}
                        </div>
                        {notif.petName && (
                          <div className="text-xs text-gray-500 dark:text-white/50 mt-0.5">
                            {notif.petName}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!notif.read && (
                          <button
                            onClick={() => markAsRead(notif.id)}
                            className="text-blue-600 hover:text-blue-700 p-1"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notif.id)}
                          className="text-gray-400 dark:text-white/40 hover:text-red-600 dark:hover:text-red-400 p-1"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 dark:text-white/70 mb-2">
                      {notif.message}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-white/50">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(notif.timestamp))}
                          </span>
                        </div>
                        {notif.professionalName && (
                          <span>• {notif.professionalName}</span>
                        )}
                      </div>

                      {notif.actionLink && (
                        <button
                          onClick={() => handleNotificationClick(notif)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                        >
                          Take Action
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
